import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Card, GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  FieldArray,
  FieldArrayRenderProps,
} from "@progress/kendo-react-form";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { MdDelete } from "react-icons/md";
import IconButton from "../../components/common/IconButton";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import {
  deleteJadaiReceiptOrderItem,
  getJadaiReceiptById,
  JadaiReceiptImageUpdate,
  deleteJadiaReceiptImage,
} from "./services/jadaireceipt.services";
import {
  setJadaiReceiptOrderItemIndex,
  setJadaiReceiptOrderSave,
  setJadaiReceiptPreviousOrder,
} from "./jadaiReceiptSlice";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { IItemCategory } from "../itemcategory/itemCategoryModel";
import { IItem } from "../item/itemModel";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { FaEdit, FaRegEye, FaSave } from "react-icons/fa";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import { useLocation } from "react-router-dom";
import { ImCross } from "react-icons/im";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import JadaiReceiptUsedByKarigarItemsArray from "./JadaiReceiptUsedByKarigarItemsArray";
import FormCheckbox from "../../components/formFields/FormCheckbox";

const JadaiReceiptOrderListArray = (
  fieldArrayRenderProps: FieldArrayRenderProps,
) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const fieldName = fieldArrayRenderProps.name;
  const jadai_receipt_guid = location?.state?.jadai_receipt_guid;
  const kargigarID =
    fieldArrayRenderProps.formRenderProps.valueGetter(`karigar_id`);

  const JadaiReceiptOrderItemIndex = useAppSelector(
    (state) => state.jadaireceipt.JadaiReceiptOrderItemIndex,
  );
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  const [imageToDeleteId, setImageToDeleteId] = React.useState<number | null>(
    null,
  );

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const ItemList = useAppSelector((state) => state.item.ItemList);
  const ItemCategoryList = useAppSelector(
    (state) => state.itemCategory.ItemCategoryList,
  );

  const handleDeleteAction = React.useCallback(
    async (index: any) => {
      if (fieldArrayRenderProps?.value[index]?.id) {
        const response = await dispatch(
          deleteJadaiReceiptOrderItem(fieldArrayRenderProps?.value[index].id),
        );
        if (response.meta.requestStatus === "fulfilled") {
          fieldArrayRenderProps.onRemove({ index: index });
          dispatch(closeDialog());
          dispatch(setJadaiReceiptOrderItemIndex(-1));
        }
      } else {
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closeDialog());
        dispatch(setJadaiReceiptOrderItemIndex(-1));
      }
    },
    [fieldArrayRenderProps],
  );

  const handleOpenDeleteDialog = (index: any) => {
    dispatch(openDialog("jadai-receipt-order-delete-dialog"));
    dispatch(setJadaiReceiptOrderItemIndex(index));
  };

  const handleGhatOrderDialog = () => {
    dispatch(openDialog("jadai-receipt-order-entry-dialog"));
    dispatch(setJadaiReceiptOrderItemIndex(999));
    dispatch(setJadaiReceiptOrderSave(true));
  };

  const handleEditOrder = (index: number) => {
    dispatch(openDialog("jadai-receipt-order-entry-dialog"));
    dispatch(setJadaiReceiptOrderItemIndex(index));
    dispatch(setJadaiReceiptOrderSave(true));
    const JadaiReceiptOrderDetails =
      fieldArrayRenderProps.formRenderProps.valueGetter(
        `${fieldName}.${index}`,
      );
    dispatch(
      setJadaiReceiptPreviousOrder(JSON.stringify(JadaiReceiptOrderDetails)),
    );
  };

  const handleSaveImage = async (index: number) => {
    const image = fieldArrayRenderProps.formRenderProps.valueGetter(
      `${fieldName}.${index}.image`,
    );
    const IsAddImage =
      fieldArrayRenderProps.formRenderProps.valueGetter(
        `${fieldName}.${index}.IsAddImage`,
      ) || false;

    const chithi_image = fieldArrayRenderProps.formRenderProps.valueGetter(
      `${fieldName}.${index}.chithi_image`,
    );

    const ID = fieldArrayRenderProps.value[index].id;
    const ImageData = new FormData();
    let binaryChitthiImage = "";
    ImageData.append("ID", ID);
    ImageData.append("IsAddImage", IsAddImage);

    if (
      typeof image !== "string" &&
      Array.isArray(image) &&
      image?.length > 0
    ) {
      image.forEach((file: any) => {
        ImageData.append("ImageGallery", file.getRawFile());
      });
    }

    if (
      typeof chithi_image !== "string" &&
      Array.isArray(chithi_image) &&
      chithi_image?.length > 0
    ) {
      binaryChitthiImage = chithi_image[0]?.getRawFile();
      ImageData.append("chithi_image", binaryChitthiImage);
    }

    if (
      (typeof image !== "string" &&
        Array.isArray(image) &&
        image?.length > 0) ||
      (typeof chithi_image !== "string" &&
        Array.isArray(chithi_image) &&
        chithi_image?.length > 0)
    ) {
      const res = await dispatch(JadaiReceiptImageUpdate(ImageData));
      if (res.meta.requestStatus === "fulfilled") {
        if (jadai_receipt_guid) {
          dispatch(getJadaiReceiptById(jadai_receipt_guid));
        }
      }
    }
  };

  const handleDeleteImage = async (id: number) => {
    const res = await dispatch(deleteJadiaReceiptImage(id));
    if (res.meta.requestStatus === "fulfilled") {
      if (jadai_receipt_guid) {
        dispatch(getJadaiReceiptById(jadai_receipt_guid));
      }
    }
  };

  const handleOpenDeleteImageDialog = (id: number) => {
    setImageToDeleteId(id);
    dispatch(openDialog("jadai-receipt-image-delete-dialog"));
  };

  const handleOpenImage = (url: string) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const isMobileScreen = windowWidth < 1300;
  return (
    <GridLayout cols={[{ width: "100%" }]}>
      <GridLayoutItem>
        <Typography.h4>Received Items</Typography.h4>
      </GridLayoutItem>
      {dialogName === "jadai-receipt-order-delete-dialog" && (
        <>
          <AppDialog>
            <>
              <Typography.h5>{"Delete Jadai Order Item"}</Typography.h5>
              <GridLayout>
                <GridLayoutItem>
                  <Typography.p>
                    Are you sure you want to delete this?
                  </Typography.p>
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    justifyContent: "end",
                  }}
                >
                  <DialogCloseButton themeColor="error" />
                  <Button
                    fillMode={"solid"}
                    themeColor={"error"}
                    onClick={() =>
                      handleDeleteAction(JadaiReceiptOrderItemIndex)
                    }
                    type="button"
                  >
                    Delete
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </>
          </AppDialog>
        </>
      )}
      {dialogName === "jadai-receipt-image-delete-dialog" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Image?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Image?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => {
                    if (imageToDeleteId) {
                      handleDeleteImage(imageToDeleteId);
                      dispatch(closeDialog());
                    }
                  }}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 &&
        fieldArrayRenderProps.value.map(
          (jadai_receipt_order: any, index: number) => (
            <GridLayoutItem key={index}>
              <Card
                className="fieldarray-card"
                style={{ backgroundColor: "#f6f6f6" }}
              >
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 10, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.order_no`}
                      label="Order No"
                      placeholder="i.e. 1542"
                      component={FormTextField}
                      validator={requiredValidator}
                      disabled={true}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.item_category_id`}
                      label="Product Category"
                      placeholder="Select Product Category"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      disabled={true}
                      options={ItemCategoryList?.map(
                        (itemcate: IItemCategory) => {
                          return {
                            value: itemcate.ID,
                            label: itemcate.category_name,
                          };
                        },
                      )}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.item_id`}
                      label="Product"
                      placeholder="Select Product"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      disabled={true}
                      options={ItemList?.map((item: IItem) => {
                        return {
                          value: item.id,
                          label: item.item_name,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  {/* <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.purity`}
                      label="Purity"
                      placeholder="0.00"
                      format="n2"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem> */}
                  {fieldArrayRenderProps.value?.[index]?.ForOpenSettings && (
                    <GridLayoutItem>
                      <Field
                        name={`${fieldName}.${index}.Dank`}
                        label="Dank Weight"
                        placeholder="0.000"
                        format="n3"
                        component={FormNumericTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                  )}
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.gross_weight`}
                      label="Gross Weight (gm)"
                      placeholder="0.000"
                      format="n3"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.stone_weight`}
                      label="Stone Weight (gm)"
                      placeholder="0.000"
                      format="n3"
                      component={FormNumericTextField}
                      // validator={requiredValidator}
                      // astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.net_weight`}
                      label="Net Weight (gm)"
                      placeholder="0.000"
                      format="n3"
                      disabled={true}
                      component={FormNumericTextField}
                      // validator={requiredValidator}
                      // astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.stone_qty`}
                      label="Stone Quantity"
                      placeholder="0.000"
                      format="n3"
                      component={FormNumericTextField}
                      // validator={requiredValidator}
                      // astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.rate`}
                      label="Rate"
                      placeholder="0.000"
                      format="n3"
                      component={FormNumericTextField}
                      // validator={requiredValidator}
                      // astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.extra_cost`}
                      label="Extra Cost"
                      placeholder="0.000"
                      format="n3"
                      component={FormNumericTextField}
                      // validator={requiredValidator}
                      // astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.total`}
                      label="Total"
                      placeholder="0.000"
                      format="n3"
                      component={FormNumericTextField}
                      // validator={requiredValidator}
                      // astrike={true}
                    />
                  </GridLayoutItem>

                  {/* <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.wastage`}
                      label="Wastage"
                      placeholder="0.00"
                      format="n2"
                      component={FormNumericTextField}
                    />
                  </GridLayoutItem> */}
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.kundan`}
                      label="Kundan"
                      placeholder="0.00"
                      format="n3"
                      component={FormNumericTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.less_jadai_verification`}
                      label="Wax Weight"
                      placeholder="0.00"
                      format="n3"
                      component={FormNumericTextField}
                    />
                  </GridLayoutItem>
                  {/* <GridLayoutItem>
                    <Field
                      name={`${fieldName}.${index}.phine`}
                      label="Phine (gm)"
                      placeholder="0.00"
                      format="n2"
                      disabled={true}
                      component={FormNumericTextField}
                    />
                  </GridLayoutItem> */}
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      display: "flex",
                      alignItems: "end",
                      justifyContent: "end",
                    }}
                  >
                    <Button
                      type="button"
                      themeColor={"tertiary"}
                      style={{ textTransform: "uppercase" }}
                      onClick={() =>
                        dispatch(
                          openDialog(`view-jadai-karigar-used-items-${index}`),
                        )
                      }
                      fillMode={"solid"}
                    >
                      Karigar Items
                    </Button>

                    {/* ITEM USED BY KARIGAR DIALOG */}
                    {dialogName ===
                      `view-jadai-karigar-used-items-${index}` && (
                      <AppDialog>
                        <div style={{ width: "80vw", overflowX: "hidden" }}>
                          <GridLayoutItem
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "end",
                              marginBottom: 10,
                            }}
                          >
                            <ImCross
                              style={{
                                color: "white",
                                background: "red",
                                borderRadius: "50%",
                                padding: "5px",
                                fontSize: "24px",
                              }}
                              onClick={() => dispatch(closeDialog())}
                            />
                          </GridLayoutItem>
                          <FieldArray
                            formRenderProps={
                              fieldArrayRenderProps.formRenderProps
                            }
                            component={JadaiReceiptUsedByKarigarItemsArray}
                            name={`${fieldName}.${index}.item_used_by_kariagr`}
                          />
                        </div>
                      </AppDialog>
                    )}
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      display: "flex",
                      alignItems: "end",
                      justifyContent: "end",
                      gap: 10,
                      position: "absolute",
                      top: 5,
                      right: 5,
                    }}
                  >
                    <IconButton
                      type="button"
                      fillMode="solid"
                      size="small"
                      title="Edit"
                      themeColor="success"
                      onClick={() => handleEditOrder(index)}
                    >
                      <FaEdit
                        className="absolute-position"
                        style={{ fontSize: "22px" }}
                      />
                    </IconButton>
                    <IconButton
                      type="button"
                      fillMode="solid"
                      size="small"
                      title="Delete"
                      themeColor="error"
                      onClick={() => handleOpenDeleteDialog(index)}
                    >
                      <MdDelete
                        className="absolute-position"
                        style={{ fontSize: "22px" }}
                      />
                    </IconButton>
                  </GridLayoutItem>
                </GridLayout>
                {fieldArrayRenderProps.value[index].id && (
                  <GridLayout
                    style={{
                      border: "1px dotted gray",
                      padding: 5,
                      marginTop: 10,
                      borderRadius: "8px",
                    }}
                  >
                    <GridLayoutItem style={{ marginTop: 5 }}>
                      <Typography.h5 style={{ margin: 0 }}>
                        Images Upload
                      </Typography.h5>
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <GridLayout
                        style={{ marginRight: 30 }}
                        gap={{ rows: 10, cols: 10 }}
                        cols={
                          isMobileScreen
                            ? [
                                { width: "33.3%" },
                                { width: "33.3%" },
                                { width: "33.3%" },
                              ]
                            : [
                                { width: "24%" },
                                { width: "24%" },
                                { width: "4%" },
                                { width: "24%" },
                                { width: "24%" },
                              ]
                        }
                      >
                        <GridLayoutItem>
                          <Field
                            imageWidth={60}
                            imageHeight={60}
                            name={`${fieldName}.${index}.image`}
                            label="Jadai Receipt Images"
                            type="file"
                            accept="image/*"
                            multiple={true}
                            component={FormImageUpload}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "end",
                            justifyContent: "end",
                            marginBottom: 8,
                          }}
                        >
                          <Field
                            name={`${fieldName}.${index}.IsAddImage`}
                            title={"Is Add Image"}
                            label={"Is Add Image"}
                            size="large"
                            component={FormCheckbox}
                            wrapperClassName="enhanced-checklist-dialog__checkbox"
                          />
                          <div
                            style={{
                              display: "flex",
                              alignItems: "end",
                              justifyContent: "end",
                              marginTop: 16,
                            }}
                          >
                            <Button
                              style={{
                                marginLeft: 5,
                              }}
                              onClick={() => handleSaveImage(index)}
                              fillMode={"solid"}
                              themeColor={"success"}
                              type="button"
                              size={"small"}
                            >
                              <FaSave style={{ fontSize: 18 }} />
                              Upload
                            </Button>

                            {/* <Button
                              style={{
                                marginLeft: 5,
                              }}
                              onClick={() =>
                                handleOpenImage(
                                  fieldArrayRenderProps.value[index]?.image,
                                )
                              }
                              disabled={
                                Array.isArray(
                                  fieldArrayRenderProps.value[index]?.image,
                                ) || !fieldArrayRenderProps.value[index]?.image
                              }
                              fillMode={"solid"}
                              themeColor={"info"}
                              type="button"
                              size={"small"}
                            >
                              <FaRegEye /> View
                            </Button> */}
                          </div>
                        </GridLayoutItem>
                        <GridLayoutItem></GridLayoutItem>

                        <GridLayoutItem>
                          <Field
                            imageWidth={60}
                            imageHeight={60}
                            name={`${fieldName}.${index}.chithi_image`}
                            label="Chithi Image"
                            type="file"
                            accept="image/*"
                            component={FormImageUpload}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem
                          style={{
                            display: "flex",
                            alignItems: "end",
                            justifyContent: "end",
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "end",
                              justifyContent: "end",
                              marginTop: 16,
                            }}
                          >
                            <Button
                              style={{
                                marginLeft: 5,
                              }}
                              onClick={() => handleSaveImage(index)}
                              fillMode={"solid"}
                              themeColor={"success"}
                              type="button"
                              size={"small"}
                            >
                              <FaSave style={{ fontSize: 18 }} /> Save
                            </Button>

                            <Button
                              style={{
                                marginLeft: 5,
                              }}
                              onClick={() =>
                                handleOpenImage(
                                  fieldArrayRenderProps.value[index]
                                    ?.chithi_image,
                                )
                              }
                              disabled={
                                Array.isArray(
                                  fieldArrayRenderProps.value[index]
                                    ?.chithi_image,
                                ) ||
                                !fieldArrayRenderProps.value[index]
                                  ?.chithi_image
                              }
                              fillMode={"solid"}
                              themeColor={"info"}
                              type="button"
                              size={"small"}
                            >
                              <FaRegEye /> View
                            </Button>
                          </div>
                        </GridLayoutItem>
                        <GridLayoutItem colSpan={isMobileScreen ? 3 : 5}>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "10px",
                              marginTop: "10px",
                            }}
                          >
                            {fieldArrayRenderProps.value[index]?.Images?.map(
                              (img: any) => (
                                <div
                                  key={img.ID}
                                  style={{ position: "relative" }}
                                >
                                  <Card
                                    style={{
                                      width: 100,
                                      height: 100,
                                      overflow: "hidden",
                                    }}
                                  >
                                    <img
                                      src={img.ImageGallery}
                                      alt="gallery"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        handleOpenImage(img.ImageGallery)
                                      }
                                    />
                                  </Card>
                                  <Button
                                    style={{
                                      position: "absolute",
                                      top: -5,
                                      right: -5,
                                      borderRadius: "50%",
                                      padding: "5px",
                                      width: "25px",
                                      height: "25px",
                                      minWidth: "25px",
                                    }}
                                    themeColor="error"
                                    fillMode="solid"
                                    type="button"
                                    onClick={() =>
                                      handleOpenDeleteImageDialog(img.ID)
                                    }
                                  >
                                    <MdDelete />
                                  </Button>
                                </div>
                              ),
                            )}
                          </div>
                        </GridLayoutItem>
                      </GridLayout>
                    </GridLayoutItem>
                  </GridLayout>
                )}
              </Card>
            </GridLayoutItem>
          ),
        )}

      <GridLayoutItem style={{ marginBottom: 10, textAlign: "left" }}>
        <Button
          fillMode={"solid"}
          themeColor={"primary"}
          disabled={!kargigarID}
          onClick={handleGhatOrderDialog}
          type="button"
        >
          Add Orders
        </Button>
      </GridLayoutItem>
    </GridLayout>
  );
};

export default JadaiReceiptOrderListArray;
